/**
 * The hook behind every "back" control in the app.
 *
 * Separate from `back.ts` so that the rule stays importable without a router: that file is what the tests
 * exercise, and this one is three lines over it.
 */

import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { backStep } from "./back.ts";

/**
 * A handler that goes where the user came from, or to `fallback` when this page was opened rather than
 * navigated to.
 *
 * The fallback is per screen and not a global default: the parent of a place is the places list, the parent of
 * the planner is the trip list, and one shared answer would be right for one of them.
 */
export function useGoBack(fallback: string): () => void {
  const navigate = useNavigate();
  const { key } = useLocation();

  return useCallback(() => {
    const step = backStep(key, fallback);
    if ("delta" in step) void navigate(step.delta);
    else void navigate(step.to);
  }, [navigate, key, fallback]);
}
